import { yupResolver } from '@hookform/resolvers/yup';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { match, useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { TransparentButton } from '../../../../../aida/components/TransparentButton';
import { FormFieldType } from '../../../../../extViewmodels';
import {
	invalidateGetEventRegistrationResponseByToken,
	useClientCreateEventRegistrationResponseMutation,
	useClientUpdateEventRegistrationResponseMutation,
	useDeleteStripeSessionMutation,
	useStripePublishableKeyByToken,
} from '../../../../../queries';
import { Pill } from '../../../../../web/components-v2/Pill/Pill';
import { Button } from '../../../../../web/components/Button';
import { Checkbox } from '../../../../../web/components/Checkbox';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../../web/components/ConfirmationDialog';
import { RadioButton } from '../../../../../web/components/RadioButton';
import { ISelectBoxOption, SelectBox } from '../../../../../web/components/SelectBox';
import { TrashIcon } from '../../../../../web/components/svgs/icons/TrashIcon';
import { WarningIcon } from '../../../../../web/components/svgs/icons/WarningIcon';
import { TextInput } from '../../../../../web/components/TextInput';
import { baseStyleSheet, bs } from '../../../../../web/styles/styles';
import { IEventRegistrationSurvey, IEventSurveyResponse, IRecipient } from '../../../../models/Api';
import { getEventRecipientFirstAndLastName } from '../utils';
import { styleSheet } from './styles';
import { areGuestNamesValid, buildCustomPropertiesSchema, getRsvpBackground, handleMutationSuccess } from './utils';

import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { LoadingLogo } from '../../../../../web/components/LoadingLogo';
import { Modal } from '../../../../../web/components/Modal';
import { CheckoutForm } from './presentation';

type Props = {
	survey: IEventRegistrationSurvey;
	onResponseSubmitted?(response: IEventSurveyResponse): void;
	surveyResponse: IEventSurveyResponse;
	routeMatch?: match<{ token?: string }>; // Make it optional for backward compatibility
	loading: boolean;
	isEdit: boolean;
	setLoading: (loading: boolean) => void;
	cancelEdit: () => void;
	onPaymentCompleted?: () => void;
	isAttending: boolean | null;
};

const usPhoneRegExp = new RegExp(/^((\+1)?[\s-]?)?\(?[1-9]\d\d\)?[\s-]?[1-9]\d\d[\s-]?\d\d\d\d$/);

const NotAttendingEventCancelOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		representedObject: true,
		title: 'Continue',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];
const PaidAttendOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isCta: true,
		representedObject: false,
		title: 'Close',
	},
];

interface IFormValues {
	isAttending: boolean | null;
	isPaid: boolean;
	bringingGuest?: boolean;
	email?: string;
	firstName?: string;
	lastName?: string;
	phone?: string;
	guests: IRecipient[];
	customProperties: Record<string, string>;
}

const DEFAULT_VALUES: IFormValues = {
	isAttending: null,
	isPaid: false,
	bringingGuest: false,
	email: '',
	guests: [{ firstName: '', lastName: '', isPaid: false }],
	firstName: '',
	lastName: '',
	phone: '',
	customProperties: {},
};
export const ErrorMessage = ({ message }: { message: string }) => (
	<p className={`${css(styleSheet.errorMessage)} ${css(styleSheet.noMargin)}`}>{message}</p>
);

export const EventSurveyForm = ({
	survey,
	onResponseSubmitted,
	surveyResponse,
	routeMatch,
	loading,
	isEdit,
	setLoading,
	cancelEdit,
	onPaymentCompleted,
	isAttending,
}: Props) => {
	const history = useHistory();

	// Use the passed routeMatch if available, or create an empty one
	const { params: urlParams = {} } = routeMatch || { params: {} };

	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('EventSurveyForm');

	const processingPayment = React.useRef<boolean>(false);

	const { data: stripePublicId } = useStripePublishableKeyByToken({
		accessToken: urlParams.token,
		refetchOnWindowFocus: false,
	});

	const [showCheckoutForm, setShowCheckoutForm] = React.useState<boolean>(false);

	const [showCancelPaidConfirmationModal, setShowCancelPaidConfirmationModal] = React.useState<boolean>(false);
	const [showPaidAttendConfirmationModal, setShowPaidAttendConfirmationModal] = React.useState<boolean>(false);
	const [ticketsOrder, setTicketsOrder] = React.useState<number>(0);
	const [paidTickets, setPaidTickets] = React.useState<number>(0);

	const validationSchema = React.useMemo(() => {
		return yup.object().shape({
			isAttending: yup.boolean().required('Response is required'),
			email: yup.string().trim().email('Invalid email').required('Email is required'),
			guests: survey?.attendeeOptions?.guestLimit
				? yup
						.array()
						.of(
							yup
								.object()
								.shape({
									firstName: yup.string(),
									lastName: yup.string(),
								})
								.test(
									'if-one-entered-both-first-and-last-must-be-defined',
									"If providing a guest's name, both their first and last name must be entered.",
									({ firstName, lastName }) => areGuestNamesValid({ firstName, lastName })
								)
						)
						.max(survey.attendeeOptions.guestLimit)
				: undefined,
			firstName: yup.string().required('First name is required'),
			lastName: yup.string().required('Last name is required'),
			phone: survey?.attendeeOptions?.requirePhoneNumber
				? yup.string().trim().matches(usPhoneRegExp, 'Must be a valid phone number').required('Phone is required')
				: yup
						.string()
						.optional()
						.trim()
						.matches(usPhoneRegExp, { excludeEmptyString: true, message: 'Must be a valid phone number' }),
			customProperties: yup.mixed().when('isAttending', {
				is: true,
				then: () =>
					survey?.customForm?.fields
						? buildCustomPropertiesSchema(
								survey.customForm.fields.map(field => ({
									...field,
									id: field.id || '', // Ensure 'id' is defined, provide a default value if necessary
								}))
							)
						: yup.object().shape({}),
				otherwise: () => yup.object().shape({}),
			}),
		});
	}, [survey]);

	const methods = useForm({
		defaultValues: DEFAULT_VALUES,
		resolver: yupResolver(validationSchema),
	});
	const { handleSubmit, control, formState, getValues, watch, setValue, register, reset } = methods;
	const formValues = watch();

	const { fields, append, remove } = useFieldArray<IFormValues>({
		control,
		name: 'guests',
	});

	const handleAttendingYes = React.useCallback(() => {
		if (
			survey?.isPaidEvent &&
			surveyResponse?.payments?.some(p => p.status === 'PaymentSucceeded') &&
			!surveyResponse?.isAttending
		) {
			setShowPaidAttendConfirmationModal(true);

			return;
		}
		setValue('isAttending', true);
	}, [setValue, survey?.isPaidEvent, surveyResponse?.isAttending, surveyResponse?.payments]);

	const handleAttendingNo = React.useCallback(() => {
		if (surveyResponse?.payments?.some(p => p.status === 'PaymentSucceeded')) {
			setShowCancelPaidConfirmationModal(true);
			return;
		}
		setValue('isAttending', false);
		setValue('guests', DEFAULT_VALUES.guests);
	}, [setValue, surveyResponse?.payments]);

	const createResponseMutation = useClientCreateEventRegistrationResponseMutation({
		onError: e => {
			toast.error(e?.systemMessage);
		},
		onSuccess: data => {
			data.isAttending = formValues.isAttending;
			handleMutationSuccess({
				onResponseSubmitted,
				successMessage: 'RSVP successfully submitted',
				data,
			});

			setLoading(false);
		},
	});
	const updateResponseMutation = useClientUpdateEventRegistrationResponseMutation({
		onError: e => {
			toast.error(e?.systemMessage);
		},
		onSuccess: data => {
			handleMutationSuccess({
				onResponseSubmitted,
				successMessage: 'RSVP successfully updated',
				data,
			});
			setLoading(false);
		},
	});

	const deleteStripeSession = useDeleteStripeSessionMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logApiError('DeleteStripeSession-Error', error);
		},
		onSuccess: async () => {
			setShowCheckoutForm(false);
		},
	});

	const resetUrlToToken = React.useCallback(() => {
		history.replace({
			pathname: `/event/${urlParams.token}`,
			search: '',
		});
	}, [history, urlParams.token]);

	const stats = survey?.stats?.responseStats?.find(x => x.category === 'Total');

	const isEventFull =
		stats && survey?.attendeeOptions?.maximumCapacity
			? stats.attending >= survey.attendeeOptions.maximumCapacity
			: false;

	const isPastEvent = React.useMemo(() => {
		const dueDateString = survey?.attendeeOptions?.registrationDeadline || survey?.eventInformation?.startTime;
		return dueDateString ? moment(dueDateString).isBefore(new Date()) : false;
	}, [survey]);

	const surveyWarningText = React.useMemo(() => {
		let text = '';
		if (isPastEvent) {
			text = 'Sorry, the event registration deadline has passed.';
		}
		if (isEventFull) {
			text = 'Sorry, the event has reached the maximum capacity of attendees.';
		}
		return `${text} Please reach out to the event organizer for details.`;
	}, [isEventFull, isPastEvent]);

	const getPaidTickets = React.useCallback(() => {
		if (surveyResponse?.payments?.some(p => p.status === 'PaymentSucceeded') || processingPayment.current) {
			// Count paid attendees and guests using the isPaid flag
			const paidAttendee = surveyResponse?.attendee?.isPaid ? 1 : 0;
			const paidGuests = surveyResponse?.guests?.filter(guest => guest.isPaid).length || 0;
			return paidAttendee + paidGuests;
		}
		return 0;
	}, [surveyResponse]);

	React.useEffect(() => {
		if (survey?.isPaidEvent) {
			setPaidTickets(getPaidTickets());
		}
	}, [survey?.isPaidEvent, getPaidTickets]);

	const getTotalTicketsOrder = React.useCallback(() => {
		if (survey?.isPaidEvent) {
			const isBringingGuest = formValues.bringingGuest;
			const guestCount = formValues.guests?.length || 0;
			let totalTickets = 1; // Always start with 1 for the attendee

			if (!isBringingGuest) {
				// If not bringing guests, only count unpaid attendee
				const attendeeIsPaid = surveyResponse?.attendee?.isPaid || false;
				return attendeeIsPaid ? 0 : 1;
			}

			if (isBringingGuest && !guestCount) {
				// If bringing guest but no guest count, add 1 for anonymous guest
				totalTickets = totalTickets + 1;
			}

			if (guestCount) {
				// Add all guests to the total
				totalTickets = totalTickets + guestCount;
			}

			// Subtract already paid tickets from the total
			const paidAttendee = surveyResponse?.attendee?.isPaid ? 1 : 0;
			const paidGuests = surveyResponse?.guests?.filter(guest => guest.isPaid).length || 0;
			totalTickets = totalTickets - paidAttendee - paidGuests;

			return Math.max(0, totalTickets);
		}
		return 0;
	}, [
		formValues.bringingGuest,
		formValues.guests?.length,
		survey?.isPaidEvent,
		surveyResponse?.attendee?.isPaid,
		surveyResponse?.guests,
	]);

	React.useEffect(() => {
		if (survey?.isPaidEvent) {
			setTicketsOrder(getTotalTicketsOrder());
		}
	}, [survey?.isPaidEvent, getTotalTicketsOrder]);

	const formattedAmount = React.useMemo(() => {
		if (survey?.attendeeOptions?.costPerAttendee) {
			const dollarAmount = new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
			}).format((survey?.attendeeOptions?.costPerAttendee * ticketsOrder) / 100);
			return dollarAmount;
		}
		return 0;
	}, [survey?.attendeeOptions?.costPerAttendee, ticketsOrder]);

	const rsvpBackground = React.useMemo(
		() => getRsvpBackground(formValues?.isAttending, isPastEvent, isEventFull),
		[formValues?.isAttending, isPastEvent, isEventFull]
	);

	const handleSave = () => {
		const values = getValues();

		const body: IEventSurveyResponse = {
			anonymousGuestCount: 0,
			attendee: {
				email: values.email.trim(),
				firstName: values.firstName.trim(),
				lastName: values.lastName.trim(),
				phone: values.phone?.trim(),
				// Preserve the isPaid flag from the existing attendee
				isPaid: surveyResponse?.attendee?.isPaid,
			},
			guests: [],
			isAttending: values.isAttending,
		};

		if (values.bringingGuest) {
			const guests = values.guests.filter(guest => guest.firstName?.trim() && guest.lastName?.trim());

			// Preserve isPaid flags for existing guests and set false for new ones
			const processedGuests = guests.map((guest, index) => {
				// If this guest exists in the original response, preserve their isPaid status
				const existingGuest = surveyResponse?.guests?.[index];
				return {
					...guest,
					isPaid: existingGuest?.isPaid || false,
				};
			});

			body.guests = processedGuests;
			body.anonymousGuestCount = values.guests.length - guests.length;

			// For paid events, convert anonymous guests to named guests
			if (survey?.isPaidEvent && body.anonymousGuestCount > 0) {
				const newGuests = [];
				for (let i = 0; i < body.anonymousGuestCount; i++) {
					newGuests.push({
						firstName: `Guest`,
						lastName: `Attending ${i + 1 + processedGuests.length}`,
						isPaid: false, // New guests are not paid
					});
				}
				body.guests = [...body.guests, ...newGuests];
				body.anonymousGuestCount = 0;
			}
		}

		if (Object.values(values.customProperties).length) {
			const fieldsWithMissingInput = survey.customForm?.fields?.filter(
				x => !x.isOptional && !x.isHidden && !values.customProperties[x.id]
			);
			if (fieldsWithMissingInput?.length && body.isAttending) {
				toast.success(`Please fill out all required fields`);
				return;
			}
			body.customProperties = values.customProperties;
		}

		// PUT/Update if we have an id
		if (surveyResponse?.id) {
			const payload = {
				...surveyResponse,
				...body,
			};
			updateResponseMutation.mutate({
				accessToken: urlParams.token,
				body: payload,
			});
			return;
		}

		// POST/Create
		createResponseMutation.mutate({ body, token: urlParams.token });
	};

	React.useEffect(() => {
		if (!urlParams.token || urlParams.token.toLocaleLowerCase() === 'unavailable') {
			setLoading(false);
			return;
		}

		const getValueFromRegisteredAndUnregisteredGuests = () => {
			const guestsRegisteredByName = surveyResponse?.guests ?? [];
			const anonymousGuestCount = surveyResponse?.anonymousGuestCount ?? 0;
			if (guestsRegisteredByName.length || surveyResponse?.anonymousGuestCount) {
				const registeredGuests = [...guestsRegisteredByName];
				// For anonymous guests, create placeholder entries with isPaid: false
				for (let i = 0; i < anonymousGuestCount; i++) {
					registeredGuests.push({ firstName: '', lastName: '', isPaid: false });
				}
				return registeredGuests;
			}
			return DEFAULT_VALUES.guests;
		};

		if (surveyResponse) {
			setLoading(true);
			const { firstName, lastName } = getEventRecipientFirstAndLastName(surveyResponse.attendee);
			const isAttendeeAttending = surveyResponse?.isAttending || isAttending;

			setValue('isAttending', isAttendeeAttending);
			setValue('email', surveyResponse?.attendee?.email);
			setValue('guests', getValueFromRegisteredAndUnregisteredGuests());
			setValue('firstName', firstName);
			setValue('lastName', lastName);
			setValue('phone', surveyResponse?.attendee?.phone);
			setValue('bringingGuest', Boolean(surveyResponse?.guests?.length || surveyResponse?.anonymousGuestCount));

			const customProperties = surveyResponse?.customProperties ?? {};
			if (customProperties) {
				Object.entries(customProperties).forEach(([key, value]) => {
					setValue(`customProperties.${key}`, value);
				});
			}
			setLoading(false);
			return;
		}
		/**
		 * No data found for a user from the token;
		 * continue as an anonymous user
		 */
		setLoading(false);
	}, [surveyResponse, setValue, urlParams.token, survey?.isPaidEvent, setLoading, isAttending]);

	const handleOpenCheckoutForm = React.useCallback(() => {
		// Recalculate ticketsOrder before opening the form
		const currentTicketsOrder = getTotalTicketsOrder();
		setTicketsOrder(currentTicketsOrder);

		setShowCheckoutForm(true);
		processingPayment.current = true;
		reset({ ...formValues });
	}, [getTotalTicketsOrder, formValues, reset]);

	const handleCloseCheckoutForm = React.useCallback(
		(id: string, cancel: boolean) => {
			setShowCheckoutForm(false);
			if (id && !cancel) {
				processingPayment.current = false;
				deleteStripeSession.mutate({
					sessionId: id,
					token: urlParams.token,
				});
			}
		},
		[deleteStripeSession, urlParams.token]
	);

	const onSuccessCloseCheckoutForm = React.useCallback(() => {
		setShowCheckoutForm(false);
		processingPayment.current = false;
		resetUrlToToken();
		cancelEdit();
		// Notify parent that payment has been completed
		onPaymentCompleted?.();

		// give enough time for the response to be updated for payment
		setTimeout(() => {
			invalidateGetEventRegistrationResponseByToken();
			reset({ ...formValues });
		}, 800);
	}, [resetUrlToToken, cancelEdit, onPaymentCompleted, reset, formValues]);

	React.useEffect(() => {
		const successfulPayments = surveyResponse?.payments?.filter(p => p.status === 'PaymentSucceeded');
		if (
			surveyResponse?.id &&
			formValues.isAttending &&
			(formState.isSubmitted || processingPayment.current) &&
			survey?.isPaidEvent &&
			!successfulPayments?.length
		) {
			const currentTicketsOrder = getTotalTicketsOrder();
			if (currentTicketsOrder > 0) {
				setTicketsOrder(currentTicketsOrder);
				setShowCheckoutForm(true);
			}
		}
	}, [
		formValues.isAttending,
		survey?.isPaidEvent,
		surveyResponse?.id,
		surveyResponse?.payments,
		getTotalTicketsOrder,
		formState.isSubmitted,
	]);

	return (
		<FormProvider {...methods}>
			{loading ? (
				<LoadingLogo size={100} />
			) : (
				<form className={css(styleSheet.rsvpForm)} onSubmit={handleSubmit(handleSave)}>
					{!isPastEvent && !isEventFull && (
						<div className={css(styleSheet.rsvpInfo)}>
							<div className={css(styleSheet.formControl)}>
								<label htmlFor='client-firstName' className={css(styleSheet.label)}>
									First Name <span className={css(styleSheet.required)}>*</span>
								</label>
								<Controller
									name='firstName'
									control={control}
									render={({ field: { ref, ...fieldProps } }) => (
										<TextInput inputId='client-firstName' name='firstName' type='text' {...fieldProps} />
									)}
								/>
								{formState.isSubmitted && formState.errors.firstName && (
									<ErrorMessage message={formState.errors.firstName.message} />
								)}
							</div>

							<div className={css(styleSheet.formControl)}>
								<label htmlFor='client-lastName' className={css(styleSheet.label)}>
									Last Name <span className={css(styleSheet.required)}>*</span>
								</label>
								<Controller
									name='lastName'
									control={control}
									render={({ field: { ref, ...fieldProps } }) => (
										<TextInput inputId='client-lastName' name='lastName' type='text' {...fieldProps} />
									)}
								/>
								{formState.isSubmitted && formState.errors.lastName && (
									<ErrorMessage message={formState.errors.lastName.message} />
								)}
							</div>

							<div className={css(styleSheet.formControl)}>
								<label htmlFor='client-email' className={css(styleSheet.label)}>
									Email <span className={css(styleSheet.required)}>*</span>
								</label>
								<Controller
									name='email'
									control={control}
									render={({ field: { ref, ...fieldProps } }) => (
										<TextInput inputId='client-email' type='text' {...fieldProps} />
									)}
								/>
								{formState.isSubmitted && formState.errors.email && (
									<ErrorMessage message={formState.errors.email.message} />
								)}
							</div>

							<div className={css(styleSheet.formControl)}>
								<label htmlFor='client-phone' className={css(styleSheet.label)}>
									Phone{' '}
									{!survey?.attendeeOptions?.requirePhoneNumber ? (
										<small style={{ fontSize: 11 }}>(Optional)</small>
									) : (
										<span className={css(styleSheet.required)}>*</span>
									)}
								</label>
								<Controller
									name='phone'
									control={control}
									render={({ field: { ref, ...fieldProps } }) => (
										<TextInput inputId='client-phone' type='text' {...fieldProps} placeholder='(999)999-9999' />
									)}
								/>
								{formState.isSubmitted && formState.errors.phone && (
									<ErrorMessage message={formState.errors.phone.message} />
								)}
							</div>
						</div>
					)}
					<div
						className={css(styleSheet.rsvpOptions)}
						style={{
							background: rsvpBackground,
						}}
					>
						{!isPastEvent && !isEventFull ? (
							<Controller
								name='isAttending'
								control={control}
								render={({ field: { ref, ...fieldProps } }) => (
									<>
										<RadioButton
											checked={fieldProps.value === true}
											className={css(styleSheet.radio)}
											id='attending-yes'
											name='attending'
											onChange={handleAttendingYes}
										>
											Yes, I plan on attending
										</RadioButton>

										<RadioButton
											checked={fieldProps.value === false}
											className={css(styleSheet.radio)}
											id='attending-no'
											name='attending'
											onChange={handleAttendingNo}
										>
											No, won&apos;t be able to make it
										</RadioButton>
									</>
								)}
							/>
						) : (
							<p className={css(styleSheet.noMargin)}>{surveyWarningText}</p>
						)}
					</div>
					{formState.isSubmitted && formState.errors.isAttending && (
						<ErrorMessage message={formState.errors.isAttending.message} />
					)}

					{!isPastEvent && !isEventFull && survey?.attendeeOptions?.guestLimit && formValues.isAttending ? (
						<div className={css(styleSheet.plusOneOptionWrap)}>
							<div className={css(styleSheet.formControl)}>
								<Controller
									name='bringingGuest'
									control={control}
									render={({ field: { ref, ...fieldProps } }) => (
										<Checkbox
											checked={fieldProps.value}
											className={css(styleSheet.checkbox)}
											id='bringingGuest'
											name='bringingGuest'
											disabled={
												surveyResponse?.payments?.some(p => p.status === 'PaymentSucceeded') &&
												Boolean(surveyResponse?.guests?.length || surveyResponse?.anonymousGuestCount)
											}
											onChange={() =>
												fieldProps.onChange(() => {
													const toggle = !fieldProps.value;
													if (!toggle) {
														setValue('guests', DEFAULT_VALUES.guests);
													}
													setValue('bringingGuest', toggle);
												})
											}
										>
											<div className={css(styleSheet.bringingGuestsLabel)}>I&apos;ll be bringing a guest</div>
										</Checkbox>
									)}
								/>
							</div>
							{formValues.bringingGuest ? (
								<>
									{fields.map((item, index) => {
										// Check if this guest is paid based on the isPaid flag
										const currentGuest = formValues.guests[index];
										const isGuestPaid = currentGuest?.isPaid || false;

										// For paid events, disable editing and removal of paid guests
										const isDisabled = survey?.isPaidEvent && isGuestPaid;

										return (
											<React.Fragment key={item.id}>
												<div className={css(styleSheet.guestInfo, isDisabled && styleSheet.disabledGuestInfo)}>
													<div className={css(styleSheet.formControl)}>
														<label htmlFor={`client-guest-${item.id}-first-name`} className={css(styleSheet.label)}>
															Guest First Name:
														</label>
														<input
															id={`client-guest-${item.id}-first-name`}
															key={item.id}
															className={`text-input ${css(styleSheet.textInput)}`}
															{...register(`guests.${index}.firstName`)}
															type='text'
															placeholder={formValues.guests[index].lastName ? '' : 'Optional'}
															disabled={isDisabled}
														/>
													</div>
													<div className={css(styleSheet.formControl)}>
														<label htmlFor={`client-guest-${item.id}-last-name`} className={css(styleSheet.label)}>
															Guest Last Name:
														</label>
														<input
															{...register(`guests.${index}.lastName`)}
															type='text'
															id={`client-guest-${item.id}-last-name`}
															key={item.id}
															className={`text-input ${css(styleSheet.textInput)}`}
															placeholder={formValues.guests[index].firstName ? '' : 'Optional'}
															disabled={isDisabled}
														/>
													</div>
													{isGuestPaid && <Pill label={`(Paid)`} size='small' variant='label' color={'green'} />}
													{!isDisabled ? (
														<TransparentButton
															type='button'
															onClick={() => {
																remove(index);
																if (!index && formValues.guests.length === 1) {
																	// If the last guest is removed, reset the bringingGuest and guests fields
																	setValue('bringingGuest', false);
																	setValue('guests', DEFAULT_VALUES.guests);
																}
															}}
														>
															<TrashIcon className={css(styleSheet.removeGuestIcon)} />
														</TransparentButton>
													) : null}
												</div>
												{formState.isSubmitted && formState.errors.guests?.[index] && (
													<div className={css(styleSheet.guestInfoError)}>
														<ErrorMessage message={formState.errors.guests[index].message} />
													</div>
												)}
											</React.Fragment>
										);
									})}
									{survey.attendeeOptions.guestLimit > fields.length ? (
										<button
											type='button'
											className={css(styleSheet.addAnotherGuestButton)}
											onClick={() => append({ firstName: '', lastName: '', isPaid: false })}
										>
											+ Add another guest
										</button>
									) : null}
								</>
							) : null}
						</div>
					) : null}
					{!survey?.customFormIsDisabled && formValues.isAttending ? (
						<>
							{survey?.customForm?.fields
								?.filter(f => !f.isHidden)
								.map(f => {
									const fieldDropdownOptions = f.options?.map(o => {
										return {
											id: o.id,
											title: o.label,
											value: o.label,
										};
									});

									return (
										<div key={f.id} className={css(styleSheet.formControlFullWidth)}>
											<label htmlFor={f.id} className={css(styleSheet.label)}>
												{f.label}
												{!f.isOptional ? (
													<>
														{' '}
														<span className={css(baseStyleSheet.required)}>*</span>
													</>
												) : null}
											</label>
											<Controller
												name={`customProperties.${f.id}`}
												control={control}
												render={({ field: { ref, ...fieldProps } }) => {
													if (f.fieldType === FormFieldType.String) {
														return (
															<>
																<TextInput
																	type='text'
																	inputId={f.id}
																	inputClassName={css(styleSheet.textInput)}
																	value={fieldProps.value || ''}
																	{...fieldProps}
																/>
																{formState?.errors.customProperties?.[f.id] && (
																	<ErrorMessage message={formState?.errors.customProperties[f.id]?.message} />
																)}
															</>
														);
													}

													if (f.fieldType === FormFieldType.Option) {
														const onSelectionChanged = (selectedOption: ISelectBoxOption<string>) => {
															setValue(`customProperties.${f.id}`, selectedOption.id);
														};
														const onRenderTriggerTitle = (selectedOption: ISelectBoxOption<string>) => {
															return <span>{selectedOption.title}</span>;
														};
														return (
															<>
																<SelectBox
																	options={fieldDropdownOptions}
																	onRenderSelectedOptionTitle={onRenderTriggerTitle}
																	onSelectionChanged={onSelectionChanged}
																	placeholder={<span>Select option</span>}
																	triggerClassName={css(styleSheet.selectBoxtriggerButton)}
																	{...fieldProps}
																	selectedOption={
																		fieldDropdownOptions?.find(x => x.id === fieldProps.value) || undefined
																	}
																/>
																{formState?.errors.customProperties?.[f.id] && (
																	<ErrorMessage message={formState?.errors.customProperties[f.id]?.message} />
																)}
															</>
														);
													}

													if (f.fieldType === FormFieldType.MultipleOptions) {
														const fieldCheckboxOptions = f.options?.map(o => {
															const fieldCheckboxSelectedOptions: string[] = fieldProps?.value?.split(',') ?? [];
															/**
															 * Method to update the value of the field
															 * @param e {React.ChangeEvent<HTMLInputElement>}
															 * sets a {String} value of the field
															 */
															const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
																const newOptions = [...fieldCheckboxSelectedOptions];
																if (e.target.checked) {
																	newOptions.push(o.id);
																} else {
																	newOptions.splice(fieldCheckboxSelectedOptions.indexOf(o.id), 1);
																}

																setValue(`customProperties.${f.id}`, `${newOptions.join(',')}`);
															};
															/**
															 * Use the onChange method to update the value of the field
															 */
															fieldProps.onChange = onChange;
															return (
																<Checkbox
																	key={o.id}
																	id={o.id}
																	className={css(styleSheet.checkbox)}
																	checked={fieldCheckboxSelectedOptions?.includes(o.id)}
																	{...fieldProps}
																>
																	<span>{o.label}</span>
																</Checkbox>
															);
														});
														return (
															<>
																{fieldCheckboxOptions}
																{formState?.errors.customProperties?.[f.id] && (
																	<ErrorMessage message={formState?.errors.customProperties[f.id]?.message} />
																)}
															</>
														);
													}

													return null;
												}}
											/>
										</div>
									);
								})}
						</>
					) : null}
					{formValues.isAttending && surveyResponse?.id && ticketsOrder > 0 ? (
						<div className={css(styleSheet.paymentInfo)}>
							<div className={css(styleSheet.divider)} />
							<section className={css(bs.flex, bs.gap2, bs.itemsCenter)}>
								<p>
									Tickets: <strong>{ticketsOrder}</strong>{' '}
								</p>
								{paidTickets ? (
									<div className={css(styleSheet.paidTickets)}>
										<Pill label={`Paid Tickets: ${paidTickets}`} variant='label' color={'green'} />
									</div>
								) : null}
							</section>
							<p className={css(styleSheet?.paymentInfoText)}>
								Subtotal: <strong>{formattedAmount}</strong>
								<br />
								<span>Taxes are calculated at checkout</span>
							</p>
						</div>
					) : null}
					{formValues.isAttending && !ticketsOrder && survey?.isPaidEvent && paidTickets > 0 ? (
						<div className={css(styleSheet.paidTickets)}>
							<Pill label={`Paid Tickets: ${paidTickets}`} variant='label' color={'green'} />
						</div>
					) : null}

					<footer className={css(styleSheet.actions)}>
						{!isPastEvent && !isEventFull ? (
							<>
								<div className={css(styleSheet.divider)} />
								<div className={css(styleSheet.buttonContainer)}>
									{surveyResponse?.id && formValues.isAttending && ticketsOrder > 0 && survey?.isPaidEvent ? (
										<Button
											type='submit'
											kind='primary'
											disabled={createResponseMutation.isLoading || updateResponseMutation.isLoading}
											isLoading={createResponseMutation.isLoading || updateResponseMutation.isLoading}
											loadingSpinnerSize='extra-small'
											label={
												formValues.isAttending && !surveyResponse?.isAttending ? 'Update Response' : 'Pay for Event'
											}
											onClick={handleOpenCheckoutForm}
										/>
									) : (
										<Button
											type='submit'
											kind='primary'
											disabled={createResponseMutation.isLoading || updateResponseMutation.isLoading}
											isLoading={createResponseMutation.isLoading || updateResponseMutation.isLoading}
											loadingSpinnerSize='extra-small'
											label={surveyResponse?.id ? 'Update Response' : 'RSVP'}
										/>
									)}
									{surveyResponse?.id && isEdit ? (
										<Button type='button' kind='reverse' label='Cancel' onClick={cancelEdit} />
									) : null}
								</div>
							</>
						) : null}
					</footer>
					<ConfirmationDialog
						key='not-attending-event-cancel-confirmation'
						icon={<WarningIcon />}
						modalProps={{
							isOpen: showCancelPaidConfirmationModal,
							onRequestClose: (_, cancel) => {
								if (cancel) {
									setShowCancelPaidConfirmationModal(false);
									return;
								}
								setValue('isAttending', false);
								setShowCancelPaidConfirmationModal(false);
								handleSave();
							},
						}}
						options={NotAttendingEventCancelOptions}
						title='Refund Warning'
					>
						<p className={css(styleSheet.warningMessage)}>
							You are changing your attendance to <strong>Not Attending</strong>.{' '}
							<span>This will not initiate a refund</span>. Please reach out to the event host for their refund policy.
						</p>
					</ConfirmationDialog>
					<ConfirmationDialog
						key='wants-to-attend-event-confirmation'
						icon={<WarningIcon />}
						modalProps={{
							isOpen: showPaidAttendConfirmationModal,
							onRequestClose: () => {
								setShowPaidAttendConfirmationModal(false);
							},
						}}
						options={PaidAttendOptions}
						title='Attendance Warning'
					>
						<p className={css(styleSheet.warningMessage)}>
							You have already responded as not attending. Please reach out to the event host to change your response.
						</p>
					</ConfirmationDialog>
					<Modal
						isOpen={showCheckoutForm}
						onRequestClose={handleCloseCheckoutForm}
						useDefaultHeader={false}
						shouldCloseOnOverlayClick={false}
					>
						{stripePublicId ? (
							<CheckoutForm
								onRequestClose={onSuccessCloseCheckoutForm}
								surveyResponse={surveyResponse}
								stripePublicId={stripePublicId}
								token={urlParams.token}
								ticketsOrder={ticketsOrder}
							/>
						) : null}
					</Modal>
				</form>
			)}
		</FormProvider>
	);
};
